import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

/**
 * Mi suscripción: el negocio consulta su estado y reporta su pago.
 *
 * Reportar no es pagar. El comprobante entra en estado pendiente y alguien lo
 * revisa a mano desde el panel del super admin, así que la pantalla dice
 * "en revisión" en todo momento y nunca da por saldada la suscripción.
 */

const MAX_BYTES = 1_400_000;   // ~1,9 MB ya en base64, el tope que acepta la API
const METODOS = ['Transferencia', 'Nequi', 'Daviplata', 'PSE', 'Bancolombia', 'Efectivo', 'Otro'];

const money = (n) => '$' + Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });
const fmtFecha = (f) => {
  if (!f) return '—';
  const d = new Date(String(f).slice(0, 10) + 'T00:00:00');
  return isNaN(d) ? f : d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const hoy = () => new Date().toISOString().slice(0, 10);

const ESTADO_PAGO = {
  pendiente: { texto: 'En revisión', clase: 'badge-pending' },
  recibido:  { texto: 'Aprobado',    clase: 'badge-active' },
  fallido:   { texto: 'Rechazado',   clase: 'badge-cancelled' },
};

export default function MiSuscripcion({ notificar }) {
  const [datos, setDatos]     = useState(null);
  const [cargando, setCarga]  = useState(true);
  const [fallo, setFallo]     = useState('');
  const [enviando, setEnv]    = useState(false);
  const [ampliada, setAmp]    = useState(null);

  const [form, setForm] = useState({
    monto: '', metodo: 'Transferencia', referencia: '', fecha_pago: hoy(), notas: '',
  });
  const [imagen, setImagen] = useState(null);   // { dataUrl, nombre, peso }

  const cargar = useCallback(async () => {
    setCarga(true);
    try {
      const r = await api.get('/suscripcion/mi');
      setDatos(r.data);
      setForm(f => ({ ...f, monto: f.monto || String(r.data.monto || '') }));
      setFallo('');
    } catch (err) {
      setFallo(err.response?.status === 404
        ? 'El servidor todavía no tiene este módulo: falta subir routes/suscripcion.js y reiniciar.'
        : (err.response?.data?.error || 'No se pudo cargar tu suscripción'));
    } finally { setCarga(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const elegirArchivo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return notificar('El comprobante debe ser una imagen', true);
    if (file.size > MAX_BYTES) {
      return notificar('La imagen pesa demasiado. Tómala con menos calidad o recórtala.', true);
    }
    const lector = new FileReader();
    lector.onload = () => setImagen({ dataUrl: lector.result, nombre: file.name, peso: file.size });
    lector.readAsDataURL(file);
  };

  const enviar = async () => {
    if (!Number(form.monto))  return notificar('Escribe el valor pagado', true);
    if (!form.fecha_pago)     return notificar('Indica la fecha del pago', true);
    if (!imagen)              return notificar('Adjunta el comprobante', true);

    setEnv(true);
    try {
      const r = await api.post('/suscripcion/pagos', {
        ...form,
        monto: Number(form.monto),
        comprobante: imagen.dataUrl,
        comprobante_nombre: imagen.nombre,
      });
      notificar(r.data?.message || 'Comprobante enviado');
      setImagen(null);
      setForm(f => ({ ...f, referencia: '', notas: '' }));
      cargar();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo enviar el comprobante', true);
    } finally { setEnv(false); }
  };

  const verComprobante = async (pagoId) => {
    try {
      const r = await api.get(`/suscripcion/comprobante/${pagoId}`);
      setAmp(r.data.imagen);
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo abrir el comprobante', true);
    }
  };

  const copiarCodigo = () => {
    navigator.clipboard?.writeText(datos.codigo_referencia)
      .then(() => notificar('Código copiado'))
      .catch(() => notificar('Copia el código a mano: ' + datos.codigo_referencia, true));
  };

  if (cargando) {
    return <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando tu suscripción...</div>;
  }
  if (fallo) {
    return (
      <div className="card" style={{ padding: '2.5rem 2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>⚠️</div>
        <p style={{ margin: '0 auto', maxWidth: '52ch', fontSize: '0.86rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{fallo}</p>
        <button className="btn btn-secondary" style={{ marginTop: 14 }} onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  const { plan, monto, vence_en: vence, dias, estado, codigo_referencia: codigo, en_revision: enRevision, pagos } = datos;

  const aviso = {
    vencida:    { clase: 'alert-error',  icono: '⚠', texto: <><strong>Tu suscripción venció hace {Math.abs(dias)} día(s).</strong> Sube el comprobante para ponerte al día. Si pasan 15 días sin pago, la cuenta se suspende y tus clientes dejan de poder reservar.</> },
    suspendida: { clase: 'alert-error',  icono: '⛔', texto: <><strong>Tu cuenta está suspendida.</strong> Sube el comprobante del pago y la reactivamos apenas lo verifiquemos.</> },
    por_vencer: { clase: 'alert-warning', icono: '⏰', texto: <><strong>Tu suscripción vence en {dias} día(s).</strong> Puedes pagar y subir el comprobante desde ya.</> },
  }[estado];

  return (
    <div>
      {/* ── estado ── */}
      {/* Dos mitades con un filete entre ellas, como el resto del panel: con
          space-between los datos se iban al borde y quedaban desconectados. */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,1fr)' }}
             className="susc-grid">
          <div style={{ padding: '1.35rem 1.5rem' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-text-faint)' }}>
              Próximo pago
            </div>
            <div style={{ fontSize: '2.1rem', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1, marginTop: 2 }}>
              {money(monto)}
            </div>
            <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', marginTop: 2 }}>
              {vence ? <>Vence el {fmtFecha(vence)}{dias != null && dias >= 0 && ` · en ${dias} día(s)`}</> : 'Sin fecha de vencimiento registrada'}
            </div>
          </div>
          <div style={{
            padding: '1.35rem 1.5rem', background: 'var(--color-surface-2)',
            borderLeft: '1px solid var(--color-border)',
          }}>
            <Dato k="Plan" v={plan || '—'} />
            <Dato k="Estado" v={
              <span style={{ color: estado === 'activa' ? 'var(--color-success)' : estado === 'por_vencer' ? 'var(--color-warning)' : 'var(--color-error)' }}>
                {{ activa: 'Activa', por_vencer: 'Por vencer', vencida: 'Vencida', suspendida: 'Suspendida', sin_fecha: 'Sin fecha' }[estado]}
              </span>
            } />
            <Dato k="Tu código" v={
              <button className="btn btn-ghost" onClick={copiarCodigo} title="Copiar código"
                      style={{ padding: '3px 9px', fontFamily: 'monospace', fontWeight: 700,
                               letterSpacing: '0.05em', color: 'var(--color-accent)',
                               background: 'var(--color-accent-subtle)', borderRadius: 6 }}>
                {codigo} ⧉
              </button>
            } />
          </div>
        </div>
      </div>

      {aviso && (
        <div className={`alert ${aviso.clase}`} style={{ marginBottom: '1rem' }}>
          <span>{aviso.icono}</span><div>{aviso.texto}</div>
        </div>
      )}

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <span>🔖</span>
        <div>
          Escribe <strong style={{ fontFamily: 'monospace' }}>{codigo}</strong> en la descripción de
          tu transferencia. Es tu código y no se repite: con él identificamos tu pago sin tener que
          adivinar de quién viene.
        </div>
      </div>

      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))' }}>
        {/* ── reportar ── */}
        <div className="card">
          <div style={{ padding: '1.25rem 1.4rem' }}>
            <h3 style={{ margin: 0 }}>Reportar un pago</h3>
            <p style={{ margin: '4px 0 16px', fontSize: '0.82rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
              Sube el comprobante de tu transferencia o pago por Nequi. Lo revisamos y te
              confirmamos por WhatsApp.
            </p>

            {enRevision ? (
              <div className="alert alert-warning" style={{ marginBottom: 0 }}>
                <span>⏳</span>
                <div>
                  <strong>Ya tienes un comprobante en revisión.</strong> Lo estamos verificando y te
                  avisamos por WhatsApp. No hace falta que subas otro.
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Valor pagado</label>
                    <input type="number" min="0" step="1" className="form-input" value={form.monto}
                           onChange={e => setForm(f => ({ ...f, monto: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Fecha del pago</label>
                    <input type="date" className="form-input" value={form.fecha_pago}
                           onChange={e => setForm(f => ({ ...f, fecha_pago: e.target.value }))} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Medio de pago</label>
                    <select className="form-select" value={form.metodo}
                            onChange={e => setForm(f => ({ ...f, metodo: e.target.value }))}>
                      {METODOS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">N° de referencia</label>
                    <input className="form-input" placeholder="El del comprobante" value={form.referencia}
                           onChange={e => setForm(f => ({ ...f, referencia: e.target.value }))} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Comprobante</label>
                  {imagen ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11, border: '1px solid var(--color-border)', borderRadius: 8, padding: '9px 11px', background: 'var(--color-surface-2)' }}>
                      <img src={imagen.dataUrl} alt="" style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 5, cursor: 'pointer' }}
                           onClick={() => setAmp(imagen.dataUrl)} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{imagen.nombre}</span>
                        <span style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)' }}>{Math.round(imagen.peso / 1024)} KB · listo para enviar</span>
                      </span>
                      <button className="btn btn-ghost btn-icon" onClick={() => setImagen(null)} title="Quitar">✕</button>
                    </div>
                  ) : (
                    <div style={{ border: '1.5px dashed var(--color-border-light)', borderRadius: 8, padding: '18px 14px', textAlign: 'center', background: 'var(--color-surface-2)' }}>
                      <div style={{ fontSize: '1.4rem' }}>🧾</div>
                      <div style={{ fontSize: '0.84rem', fontWeight: 500, marginTop: 5 }}>Sube la foto del comprobante</div>
                      <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                        Captura de pantalla o foto del recibo
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                          📷 Tomar foto
                          <input type="file" accept="image/*" capture="environment" hidden onChange={elegirArchivo} />
                        </label>
                        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                          📎 Elegir archivo
                          <input type="file" accept="image/*" hidden onChange={elegirArchivo} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Nota (opcional)</label>
                  <input className="form-input" placeholder="Ej. pagué desde otra cuenta" value={form.notas}
                         onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
                </div>

                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
                        onClick={enviar} disabled={enviando}>
                  {enviando ? 'Enviando...' : 'Enviar comprobante'}
                </button>
                <p style={{ margin: '9px 0 0', fontSize: '0.76rem', color: 'var(--color-text-muted)', textAlign: 'center', lineHeight: 1.5 }}>
                  El pago queda <strong>en revisión</strong> hasta que lo aprobemos.
                  Tu servicio no se interrumpe mientras tanto.
                </p>
              </>
            )}
          </div>
        </div>

        {/* ── historial ── */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '1.25rem 1.4rem 0.6rem' }}>
            <h3 style={{ margin: 0 }}>Pagos reportados</h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
              En qué va cada comprobante que has subido.
            </p>
          </div>
          {pagos.length === 0 ? (
            <div style={{ padding: '2.5rem 1.4rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.86rem' }}>
              Todavía no has reportado ningún pago.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', minWidth: 420 }}>
                <thead>
                  <tr>
                    <th>Fecha</th><th>Medio</th>
                    <th style={{ textAlign: 'right' }}>Valor</th><th>Estado</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.map(p => {
                    const e = ESTADO_PAGO[p.estado] || { texto: p.estado, clase: '' };
                    return (
                      <tr key={p.id}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{fmtFecha(p.fecha_pago)}</td>
                        <td style={{ fontSize: '0.82rem' }}>
                          {p.metodo || '—'}
                          {p.referencia && <div style={{ fontSize: '0.73rem', color: 'var(--color-text-muted)' }}>Ref. {p.referencia}</div>}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{money(p.monto)}</td>
                        <td>
                          <span className={`badge ${e.clase}`} style={{ fontSize: '0.68rem' }}>{e.texto}</span>
                          {p.estado === 'fallido' && p.motivo_fallo && (
                            <div style={{ fontSize: '0.74rem', color: 'var(--color-error)', marginTop: 3, maxWidth: 220, lineHeight: 1.4 }}>
                              {p.motivo_fallo}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {p.tiene_comprobante && (
                            <button className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: '0.78rem' }}
                                    onClick={() => verComprobante(p.id)}>Ver</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {ampliada && (
        <div onClick={() => setAmp(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.75)', zIndex: 1200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', cursor: 'zoom-out',
        }}>
          <img src={ampliada} alt="Comprobante" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}

function Dato({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center',
                  padding: '6px 0', borderBottom: '1px solid var(--color-border)', fontSize: '0.84rem' }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{k}</span>
      <strong style={{ fontWeight: 600, textAlign: 'right' }}>{v}</strong>
    </div>
  );
}
